import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Circle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const VerifyOTP = () => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [cooldown, setCooldown] = useState(60);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Cooldown timer effect
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    // Get phone number from session storage
    const stored = sessionStorage.getItem("phoneNumber");
    if (!stored) {
      navigate("/login");
      return;
    }
    setPhoneNumber(stored);
  }, [navigate]);

  const formatPhoneNumber = (phone: string) => {
    // Display phone as-is (already has + prefix)
    return phone;
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a 6-digit OTP code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Verify OTP via edge function
      const response = await supabase.functions.invoke('verify-otp', {
        body: { phoneNumber, otp },
      });

      // Handle error responses - extract message from various formats
      if (response.error || response.data?.error) {
        let errorMessage = "";
        let errorTitle = "Verification Failed";
        
        // Try to get error from response data first
        if (response.data?.error) {
          errorMessage = response.data.error;
        } 
        // Try to parse error from the FunctionsHttpError context
        else if (response.error?.context) {
          try {
            const errorBody = await response.error.context.json();
            if (errorBody?.error) {
              errorMessage = errorBody.error;
            }
          } catch {
            // If parsing fails, it's an unexpected error
          }
        }
        
        // If no specific error message, show generic error
        if (!errorMessage) {
          errorTitle = "Something Went Wrong";
          errorMessage = "Unable to verify OTP. Please check your connection and try again.";
        }
        
        toast({
          title: errorTitle,
          description: errorMessage,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (response.data?.success) {
        // Store session in localStorage
        localStorage.setItem("customAuth", JSON.stringify({
          authenticated: true,
          phoneNumber: phoneNumber,
          sessionToken: response.data.sessionToken,
          userId: response.data.userId,
          expiresAt: response.data.expiresAt,
          timestamp: Date.now()
        }));

        // Clear session storage
        sessionStorage.removeItem("phoneNumber");

        toast({
          title: "Success!",
          description: "You've been successfully authenticated",
        });

        setLoading(false);
        navigate("/");
      } else {
        toast({
          title: "Authentication Failed",
          description: "Unable to create session. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (cooldown > 0) return;
    
    setResending(true);
    
    const response = await supabase.functions.invoke('send-otp', {
      body: { phoneNumber },
    });

    if (response.error || response.data?.error) {
      toast({
        title: "Error",
        description: response.data?.error || response.error?.message || "Failed to resend OTP. Please try again.",
        variant: "destructive",
      });
    } else {
      setCooldown(60); // Reset cooldown timer
      toast({
        title: "OTP Resent",
        description: "A new 6-digit OTP has been sent to your phone number",
      });
    }
    
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-2">
          <Circle className="w-6 h-6 text-primary fill-primary" />
          <span className="text-lg font-semibold">SurgyDischarge</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Enter OTP</h1>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit OTP that we have sent to{" "}
              <span className="font-semibold">{formatPhoneNumber(phoneNumber)}</span>
            </p>
          </div>

          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
                disabled={loading}
              >
                <InputOTPGroup className="gap-2">
                  <InputOTPSlot index={0} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={1} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={2} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={3} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={4} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={5} className="w-12 h-12 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Didn't receive code? </span>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={loading || resending || cooldown > 0}
                className="text-primary font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Now"}
              </button>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-[hsl(207,100%,45%)] hover:bg-[hsl(207,100%,40%)] text-white shadow-lg transition-all duration-150 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md" 
              disabled={loading || otp.length !== 6}
            >
              {loading ? "Verifying..." : "Verify"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
